import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';

import { Block } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';
import { IconName, Illustration } from '@klp/design/Illustrations';

import { useRemoveCustomerMutation } from '../rtkApi';

interface IRemoveCustomerProps {
    customerNumber: string;
    email: string;
    disabled?: boolean;
    onSuccess: () => void;
}

export const RemoveCustomer: React.FC<IRemoveCustomerProps> = ({
    customerNumber,
    email,
    disabled,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [removeCustomer] = useRemoveCustomerMutation();

    const handleRemoveCustomer = () => {
        setDisplayDialog(true);
    };

    const handleRemoveCustomerClose = () => {
        if (!isLoading) {
            setDisplayDialog(false);
        }
    };

    const handleRemoveCustomerConfirmed = async () => {
        setIsLoading(true);
        try {
            await removeCustomer({ customerNumber, email }).unwrap();
            onSuccess();
            setDisplayDialog(false);
        } catch (error) {
            // Error handling is done by RTK Query
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Block>
                <Button
                    size="small"
                    startIcon={
                        <Illustration
                            icon={IconName.CIRCLE_WARNING}
                            klpColor={disabled ? 'mediumLight' : 'main'}
                        />
                    }
                    onClick={handleRemoveCustomer}
                    disabled={disabled}
                >
                    {customerNumber}
                </Button>
            </Block>
            <Dialog open={displayDialog} onClose={handleRemoveCustomerClose}>
                <DialogTitle>{t('userSearch.removeCustomerTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('userSearch.removeCustomerQuestion', {
                            email: email,
                            customer: customerNumber,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        translation="general.decline"
                        onClick={handleRemoveCustomerClose}
                        disabled={isLoading}
                    />
                    <ActionButton
                        actionType="forward"
                        translation="general.confirm"
                        onClick={handleRemoveCustomerConfirmed}
                        isLoading={isLoading}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
